import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from '../../../context/NotificationContext';   
import { Card, Table, Input, Button, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Search } = Input;

const SalesChannelList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();
  // State management
  const [loading, setLoading] = useState(false);
  const [salesChannels, setSalesChannels] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} sales channels`,
  });

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
  });

  const getSalesChannels = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        console.log("Fetching sales channels with params:", requestParams);
        const response = await masterService.getSalesChannels(requestParams);
        console.log("Sales Channels API Response:", response);

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const salesChannelsData =
            response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          console.log("Sales Channels data:", salesChannelsData);
          console.log("Pagination data:", paginationData);

          // Ensure salesChannelsData is an array
          if (Array.isArray(salesChannelsData)) {
            setSalesChannels(salesChannelsData);
          } else {
            console.error(
              "Sales Channels data is not an array:",
              salesChannelsData
            );
            setSalesChannels([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch sales channels" });
          setSalesChannels([]);
        }
      } catch (error) {
        console.error("Error fetching sales channels:", error);
        notify.error({ message: "Failed to fetch sales channels" });
        setSalesChannels([]);
      } finally {
        setLoading(false);
      }
    },
    [filters.searchText]
  );

  // Load sales channels data
  useEffect(() => {
    setPageTitle("Sales Channels");
    getSalesChannels();
  }, [getSalesChannels, setPageTitle]);

  // Handle search
  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getSalesChannels({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getSalesChannels({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current,
    });
  };

  // Handle edit sales channel
  const handleEditSalesChannel = (scId) => {
    navigate(`/sales-channel/edit/${scId}`);
  };

  // Handle add sales channel
  const handleAddSalesChannel = () => {
    navigate("/sales-channel/add/new");
  };

  // Table columns
  const columns = [
    {
      title: "Channel Name",
      dataIndex: "sc_name",
      key: "sc_name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.sc_name.localeCompare(b.sc_name),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "sc_is_active",
      key: "sc_is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditSalesChannel(record.sc_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Sales Channels ({pagination.total})
          </Title>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 300,
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <Search
              placeholder="Search sales channels..."
              allowClear
              onSearch={handleSearch}
              style={{ width: 320, maxWidth: "40vw" }}
              enterButton={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddSalesChannel}
              size="middle"
            >
              Add Sales Channel
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={salesChannels}
          rowKey="sc_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{
            emptyText: "No sales channels found",
            filterConfirm: "OK",
            filterReset: "Reset",
            filterEmptyText: "No filters",
          }}
        />
      </Card>
  );
};

export default SalesChannelList;
